"use client";

import React, { createContext, useContext, ReactNode } from "react";
import type {
  UserWithRelations,
  LoginCredentials,
  CreateUserData,
} from "@/lib/auth/types";
import { useAuthActions } from "@/hooks/useAuthActions";
import { useAuthStatus } from "@/hooks/useAuthStatus";

interface AuthContextType {
  user: UserWithRelations | null;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: CreateUserData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
  accessToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export AuthContext for direct access if needed
export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const {
    user,
    loading,
    error,
    accessToken,
    login,
    register,
    logout,
    refreshToken,
    clearError,
    setUser,
    setAccessToken,
    setLoading,
  } = useAuthActions();

  // Initialize auth status check only on client side
  useAuthStatus({ setUser, setAccessToken, setLoading });

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshToken,
    clearError,
    accessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  // During SSR or initial hydration, return a safe default
  if (context === undefined) {
    return {
      user: null,
      loading: true,
      error: null,
      login: async () => {},
      register: async () => {},
      logout: async () => {},
      refreshToken: async () => false,
      clearError: () => {},
      accessToken: null,
    };
  }

  return context;
}
